<template lang="pug">
div
  v-card.round-xl(outlined)
    //- profile section
    v-row.pa-4(dense)
      v-col.shrink
        v-img.round-xl(v-if="patient.profile_image" width="60" height="80" :src="$options.filters.mediumImage(patient.profile_image.file)" cover)
          template(v-slot:placeholder)
            v-row(class="fill-height ma-0" align="center" justify="center")
              v-progress-circular(indeterminate color="grey")
        v-card.neutral300.fill-height.round-xl(v-else flat width="60" height="80")
          div.d-flex.align-center.justify-center.fill-height
            v-icon(size="30") $yzi-user-03
      v-col
        div.text-sm.font-medium.neutral700--text {{ patient.name }}
        div.text-xs.neutral500--text(v-if="patient.dob") {{ $options.filters.getAgeByYearMonthDay(patient.dob) | displayAgeByYearMonthDay }} old
        div.text-xs.neutral500--text.mt-1 {{ patient.ic }}
      v-col.shrink
        y-button(label="View profile" outlined color="neutral400" text-color="neutral700" size="xxs" height="24" unset-min-width button-class="px-2" @click="openProfileWindow")
    v-divider
    //- billing section
    div.pa-4
      billing-section
  //- reservations card
  v-card.round-xl.mt-2(outlined :loading="isFetchingReservations")
    div.pa-4
      div.d-flex.align-center
        div.text-xs.font-semibold.neutral500--text Reservations
        v-spacer
        template(v-if="reservationsSummary")
          y-button(label="View all" outlined color="neutral400" text-color="neutral700" size="xxs" height="24" unset-min-width button-class="px-2" @click="onViewAllReservations")
          v-divider.neutral400.mx-2(vertical)
        y-button(label="Add new" size="xxs" height="24" unset-min-width button-class="px-2" dark @click="onAddNewReservation")
      template(v-if="reservationsSummary")
        v-row.mt-0
          v-col(cols="6")
            div.text-xs.neutral700--text Number of reservations
            div.text-g6.neutral700--text.mt-1 {{ reservationsSummary.count }}
          v-divider(vertical)
          v-col(cols="6")
            div.text-xs.neutral700--text Total outstanding
            div.text-g6.yellow600--text.mt-1 {{ reservationsSummary.outstanding != null ? $options.filters.displayPrice(reservationsSummary.outstanding, $currency) : '-' }}
      div.text-xs.font-medium.neutral500--text.text-center.mt-6(v-else) No reservations

  //- pre-orders card
  v-card.round-xl.mt-2(outlined :loading="isFetchingPreorders")
    div.pa-4
      div.d-flex.align-center
        div.text-xs.font-semibold.neutral500--text Pre-orders
        v-spacer
        template(v-if="preordersSummary")
          y-button(label="View all" outlined color="neutral400" text-color="neutral700" size="xxs" height="24" unset-min-width button-class="px-2" @click="onViewAllPreorders")
          v-divider.neutral400.mx-2(vertical)
        y-button(label="Add new" size="xxs" height="24" unset-min-width button-class="px-2" dark @click="onAddNewPreorder")
      template(v-if="preordersSummary")
        v-row.mt-0
          v-col(cols="6")
            div.text-xs.neutral700--text Number of pre-orders
            div.text-g6.neutral700--text.mt-1 {{ preordersSummary.count }}
          v-divider(vertical)
          v-col(cols="6")
            div.text-xs.neutral700--text Total outstanding
            div.text-g6.yellow600--text.mt-1 {{ preordersSummary.outstanding != null ? $options.filters.displayPrice(preordersSummary.outstanding, $currency) : '-' }}
      div.text-xs.font-medium.neutral500--text.text-center.mt-6(v-else) No pre-orders

  //- outstanding invoice card
  v-card.round-xl.mt-2(v-if="showOutstandingBills && outstandingInvoice" outlined)
    div.pa-4
      div.d-flex.align-center
        div.text-xs.font-semibold.neutral500--text Outstanding bill
        v-spacer
        y-button(label="View all" outlined color="neutral400" text-color="neutral700" size="xxs" height="24" unset-min-width button-class="px-2" @click="showOutstandingBillsDialog = true")
      v-row.mt-0
        v-col(cols="6")
          div.text-xs.neutral700--text Number of invoices
          div.text-g6.neutral700--text.mt-1 {{ outstandingInvoice.count }}
        v-divider(vertical)
        v-col(cols="6")
          div.text-xs.neutral700--text Total outstanding
          div.text-g6.yellow600--text.mt-1 {{ outstandingInvoice.amount | displayPrice($currency) }}
  //- appointment card
  v-card.round-xl.mt-2(v-if="Auth('component', '/admin/clinic/consultation#set_appointment')" outlined :loading="isFetchingAppt")
    div.pa-4
      div.d-flex.align-center
        div.text-xs.font-semibold.neutral500--text Upcoming appointment
        v-spacer
        y-button(label="View all" outlined color="neutral400" text-color="neutral700" size="xxs" height="24" unset-min-width button-class="px-2" @click="showUpcomingAppointmentDialog = true")
        v-divider.neutral400.mx-2(vertical)
        y-button(label="Add new" size="xxs" height="24" unset-min-width button-class="px-2" @click="openSetAppointmentDialog")
      v-row.mt-3(v-if="appointment" no-gutters)
        v-col(cols="3")
          div.text-g9.neutral700--text {{ appointment.start | humanizeDate }}
        v-col(cols="9")
          v-alert.my-0.py-0(border="left" colored-border dense color="blue500" style="background-color: var(--v-neutral100);")
            div.text-sm.font-medium.neutral700--text {{ getAppointmentProviderServices(appointment) }}
            div.text-xs.neutral500--text(v-if="appointment.time_range && appointment.time_range.length") {{ appointment.time_range[0] | displayTime2 }} - {{ appointment.time_range[1] | displayTime2 }} 
      div.text-xs.font-medium.neutral500--text.text-center.mt-6(v-else) No upcoming appointment
  //- marketing card
  template(v-if="Auth('root', 'subscribe_marketing')")
    checkout-offers-card.mt-2
    checkout-loyalty-card.mt-2

  .dialogs
    view-reservation-dialog(v-if="showViewReservationDialog" :open.sync="showViewReservationDialog" :patient="patient")
    view-reservation-dialog(v-if="showViewPreorderDialog" :open.sync="showViewPreorderDialog" :patient="patient" type="preorder")
    outstanding-bill-dialog(v-if="showOutstandingBillsDialog" :open.sync="showOutstandingBillsDialog" :invoices="outstandingInvoice.results" @selected="OpenOutstandingInvoice($event)")
    upcoming-appointment-dialog(v-if="showUpcomingAppointmentDialog" :open.sync="showUpcomingAppointmentDialog" @closed="fetchAppointments")
    set-appointment-dialog(v-if="showSetAppointmentDialog" :open.sync="showSetAppointmentDialog" :patient="patient" @advance="openAdvanceAppointmentDialog" @closed="fetchAppointments")
    add-advance-appointment(v-if="showAdvAppointmentDialog" :open.sync="showAdvAppointmentDialog" :patient="patient" @reset="fetchAppointments")
    y-dialog(v-if="showOutstandingInvoice" v-model="showOutstandingInvoice" max-width="500" persistent fluid scrollable content-class="round-2xl")
      v-card
        v-card-text.pa-6
          div.d-flex.align-center
            div.text-xl.font-medium.neutral700--text Open invoice {{ `#${selectedOutstandingInvoice.rid}` }}
            v-spacer
            v-btn(text icon @click="closeOutstandingInvoice")
              v-icon(color="neutral700" :size="24") mdi-close
          div.text-md.neutral700--text.mt-4 You will access another invoice. Would you like to open it in this tab, or would you prefer to open a new tab instead?
        v-divider
        v-card-actions.pa-6
          v-row
            v-col
              y-button(label="This tab" color="neutral700" outlined size="md" block @click="openInvoiceThisTab")
            v-col
              y-button(label="New tab" text-color="neutral100" color="neutral700" size="md" block dark @click="openInvoiceNewTab")

</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import BillingSection from '@/components/clinic/checkout/BillingSection.vue'
import OutstandingBillDialog from '@/components/clinic/checkout/OutstandingBillDialog.vue'
import UpcomingAppointmentDialog from '@/components/clinic/checkout/UpcomingAppointmentDialog.vue'
import SetAppointmentDialog from '@/components/clinic/dialogs/SetAppointmentDialog.vue'
import AddAdvanceAppointment from '@/components/clinic/dialogs/AddAdvanceAppointment.vue'
import CheckoutOffersCard from '@/components/clinic/checkout/marketing/CheckoutOffersCard.vue'
import CheckoutLoyaltyCard from '@/components/clinic/checkout/marketing/CheckoutLoyaltyCard.vue'
import ViewReservationDialog from '@/components/clinic/inventory/ViewReservationDialog.vue'

export default {
  components: { BillingSection, OutstandingBillDialog, UpcomingAppointmentDialog, CheckoutOffersCard, CheckoutLoyaltyCard, SetAppointmentDialog, AddAdvanceAppointment, ViewReservationDialog },
  data () {
    return {
      isFetchingAppt: false,
      isFetchingReservations: false,
      isFetchingPreorders: false,
      showOutstandingBillsDialog: false,
      showUpcomingAppointmentDialog: false,
      showSetAppointmentDialog: false,
      showAdvAppointmentDialog: false,
      showOutstandingInvoice: false,
      showViewReservationDialog: false,
      showViewPreorderDialog: false,
      outstandingInvoice: null,
      selectedOutstandingInvoice: null,
      appointment: null,
      reservationsCount: 0,
      preordersCount: 0,
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      leadPid: 'treatment/getLeadPid',
      patient: 'treatment/getPatient',
      hqSelectedCompany: 'hq/getSelectedCompany',
      isHq: 'hq/getIsHq'
    }),
    showOutstandingBills () {
      // Always show outstanding bills for new checkout registration
      return true
    },
    reservationsSummary () {
      if (!this.reservationsCount) return null
      // TODO: replace outstanding with API-provided total when available
      return { count: this.reservationsCount, outstanding: null }
    },
    preordersSummary () {
      if (!this.preordersCount) return null
      // TODO: replace outstanding with API-provided total when available
      return { count: this.preordersCount, outstanding: null }
    },
  },
  mounted () {
    if (this.patient.id) {
      this.fetchAllData()
    }
  },
  watch: {
    'patient.id' (newVal, oldVal) {
      if (newVal && !oldVal) {
        this.fetchAllData()
      }
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchAllData () {
      this.fetchOutstandingInvoices()
      this.fetchAppointments()
      this.fetchReservations()
      this.fetchPreorders()
    },
    fetchOutstandingInvoices () {
      let params = {
        company: this.isHq ? this.hqSelectedCompany.id : this.company.id,
        paid: false,
        form_type: 'registration',
        customer: this.patient.id,
        page_size: -1
      }
      this.$axios.get(`/invoices`, { params })
        .then(resp => {
          if (resp.data && resp.data.results.length > 0) {
            const results = resp.data.results.filter((result) => result.pid !== this.leadPid)
            if (results.length > 0) {
              this.outstandingInvoice = {
                id: this.patient.id,
                amount: results.reduce((sum, a) => { return sum + (a.total_amount - a.total_paid) }, 0),
                count: results.length,
                results,
              }
            }
          }
        })
        .catch(() => {})
    },
    fetchAppointments () {
      this.isFetchingAppt = true
      let params = {
        page_size: 1,
        patient: this.patient.id,
        statuses: 'new,processed',
        ordering: 'start',
      }
      this.$axios.get(`/clinics/${this.company.slug}/appointments`, { params })
        .then(resp => {
          if (resp.data.results.length > 0) {
            this.appointment = resp.data.results[0]
          }
        })
        .catch(() => {})
        .finally(() => {
          this.isFetchingAppt = false
        })
    },
    getAppointmentProviderServices (appointment) {
      let data = []
      if (appointment.provider && appointment.provider.name) {
        data.push(appointment.provider.name)
      }
      if (appointment.services.length) {
        data = [...data, ...appointment.services.map(s => s.name)]
      }
      return data.join(', ')
    },
    openProfileWindow() {
      const link = this.isHq ? `/admin/hq/patient/${this.patient.ic}` : `/admin/clinic/patient/${this.patient.id}`
      window.open(link, '_blank')
    },
    openInvoiceThisTab () {
      this.$router.push(`/admin/clinic/checkout/${this.selectedOutstandingInvoice.pid}`)
      this.$router.go()
      this.closeOutstandingInvoice()
    },
    openInvoiceNewTab () {
      const link = this.$router.resolve(`/admin/clinic/checkout/${this.selectedOutstandingInvoice.pid}`)
      window.open(link.href, '_blank')
      this.closeOutstandingInvoice()
    },
    OpenOutstandingInvoice (invoice) {
      this.selectedOutstandingInvoice = invoice
      this.showOutstandingInvoice = true
    },
    openSetAppointmentDialog () {
      this.showSetAppointmentDialog = true
    },
    openAdvanceAppointmentDialog () {
      this.showSetAppointmentDialog = false
      this.showAdvAppointmentDialog = true
    },
    fetchReservations () {
      this.isFetchingReservations = true
      const params = { patient: this.patient.id, status: 'active', page_size: 1 }
      this.$axios.get(`/clinics/${this.company.slug}/reservations`, { params })
        .then(resp => {
          this.reservationsCount = resp.data.count || 0
        })
        .catch(() => {})
        .finally(() => {
          this.isFetchingReservations = false
        })
    },
    fetchPreorders () {
      this.isFetchingPreorders = true
      const params = { patient: this.patient.id, status: 'active', page_size: 1 }
      this.$axios.get(`/clinics/${this.company.slug}/preorders`, { params })
        .then(resp => {
          this.preordersCount = resp.data.count || 0
        })
        .catch(() => {})
        .finally(() => {
          this.isFetchingPreorders = false
        })
    },
    onAddNewReservation () {
      this.$router.push({ name: 'ClinicReservationNew', params: { patientId: String(this.patient.id), origin: 'ClinicCheckout', data: { params: { pid: this.leadPid } } } })
    },
    onAddNewPreorder () {
      this.$router.push({ name: 'ClinicPreorderNew', params: { patientId: String(this.patient.id), origin: 'ClinicCheckout', data: { params: { pid: this.leadPid } } } })
    },
    onViewAllReservations () {
      this.showViewReservationDialog = true
    },
    onViewAllPreorders () {
      this.showViewPreorderDialog = true
    },
    closeOutstandingInvoice () {
      this.showOutstandingInvoice = false
      this.selectedOutstandingInvoice = null
    },
  },
}
</script>

<style lang="scss" scoped>

</style>